import React from "react";
import { VALUES } from "../data";
import { SectionLabel, SectionTitle, SectionSub } from "../components/ui/Typography";
import { BtnOrange, BtnOutline } from "../components/ui/Buttons";
import CTABanner from "../components/ui/CTABanner";
import useScrollFade from "../hooks/useScrollFade";

const StatTile = ({ val, label, accent }) => (
  <div className="bg-white/8 border border-white/12 rounded-2xl p-6 text-center">
    <div className={`font-head font-black text-4xl ${accent || "text-white"}`}>{val}</div>
    <div className="text-white/45 text-sm mt-1">{label}</div>
  </div>
);

const ValueCard = ({ v, delay }) => {
  const ref = useScrollFade();
  return (
    <div ref={ref} className="fade-in-section card-hover bg-white border border-slate-200 rounded-2xl p-7 text-center" style={{ transitionDelay: `${delay}ms` }}>
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${v.bg} flex items-center justify-center text-2xl mx-auto mb-4`}>
        {v.icon}
      </div>
      <h3 className="font-head font-bold text-slate-900 mb-2">{v.title}</h3>
      <p className="text-slate-500 text-sm leading-relaxed">{v.desc}</p>
    </div>
  );
};

const AboutPage = ({ navigate }) => (
  <div className="page-enter">
    {/* Hero */}
    <section className="bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden pt-32 pb-20">
      <div className="hero-grid-bg absolute inset-0 opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionLabel light>About Us</SectionLabel>
            <h1 className="font-head font-black text-5xl md:text-6xl text-white mt-3 mb-5 tracking-tight leading-tight">
              We Are<br /><span className="gradient-text">Eminent Solutions</span>
            </h1>
            <p className="text-white/65 text-lg leading-relaxed mb-8">
              A forward-thinking software house committed to harnessing the power of Artificial Intelligence to solve real-world business problems. Our team of experienced developers, data scientists, and business strategists work together to deliver impactful solutions that drive growth and innovation.
            </p>
            <div className="flex flex-wrap gap-3">
              <BtnOrange onClick={() => navigate("contact")}>Work With Us</BtnOrange>
              <BtnOutline onClick={() => navigate("services")}>Our Services</BtnOutline>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StatTile val="2020"  label="Founded in Karachi" accent="text-white" />
            <StatTile val="50+"   label="Projects Delivered"  accent="text-orange-400" />
            <StatTile val="30+"   label="Enterprise Clients"  accent="text-blue-300" />
            <StatTile val="4"     label="Core AI Products"    accent="text-emerald-400" />
          </div>
        </div>
      </div>
    </section>

    {/* Mission & Vision */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl p-10 text-white">
            <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center text-2xl mb-5">⚡</div>
            <h3 className="font-head font-bold text-2xl mb-4">Our Mission</h3>
            <p className="text-white/80 text-base leading-relaxed">
              To empower industries by delivering intelligent, AI-driven software solutions that streamline operations, reduce manual effort, and accelerate digital transformation.
            </p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-10 border border-slate-200">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl mb-5">👁️</div>
            <h3 className="font-head font-bold text-2xl text-slate-900 mb-4">Our Vision</h3>
            <p className="text-slate-600 text-base leading-relaxed">
              To become a global leader in AI-powered software innovation — a trusted partner for organizations seeking smarter ways to operate, grow, and compete in the digital age.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="text-center mb-12">
          <SectionLabel>Our Values</SectionLabel>
          <SectionTitle>What We <span className="text-blue-600">Believe In</span></SectionTitle>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => <ValueCard key={v.title} v={v} delay={i * 80} />)}
        </div>
      </div>
    </section>

    {/* Team strip */}
    <section className="py-16 bg-gradient-to-br from-slate-950 to-[#0d2151]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
        <div className="text-white/30 text-xs font-head font-bold uppercase tracking-widest mb-6">Our expertise spans</div>
        <div className="flex flex-wrap justify-center gap-3">
          {["Artificial Intelligence","Machine Learning","Computer Vision","Natural Language Processing","Data Engineering","ERP Integration","Mobile Development","Cloud Architecture"].map(t => (
            <span key={t} className="text-sm font-head font-semibold px-4 py-2 rounded-full glass text-white/70 border-white/10">
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>

    <CTABanner
      navigate={navigate}
      title="Join Our Journey"
      sub="We're always looking for talented people passionate about AI and technology."
      btn1="See Open Positions"
      btn2="Partner With Us"
      page1="contact"
      page2="contact"
    />
  </div>
);

export default AboutPage;
